import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Admin.css';

const Admin = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});
  const [orderThumbnails, setOrderThumbnails] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentOrdersLoading, setRecentOrdersLoading] = useState(false);
  const [recentOrdersVisibleCount, setRecentOrdersVisibleCount] = useState(20);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setVisibleCounts({}); // Reset visible counts on new search
    setOrderThumbnails({}); // Reset thumbnails on new search

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/orders/admin/search?q=${encodeURIComponent(searchQuery.trim())}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have permission to access this page');
        }
        throw new Error('Failed to search');
      }

      const data = await response.json();
      setResults(data);
      
      // Load thumbnails for order items and designs
      const thumbnailPromises = [];
      
      // Load thumbnails for order items
      if (data.orders) {
        Object.keys(data.orders).forEach(categoryKey => {
          data.orders[categoryKey].forEach(order => {
            order.items?.forEach((item, itemIndex) => {
              if (hasDesign(item)) {
                const key = `order-${order.orderId}-${itemIndex}`;
                thumbnailPromises.push(
                  getOrderItemThumbnail(item, order.orderId, itemIndex).then(thumbnail => {
                    console.log(`[DEBUG] Thumbnail result for ${key}:`, thumbnail ? `Found (length: ${thumbnail.length})` : 'null');
                    if (thumbnail) {
                      setOrderThumbnails(prev => ({ ...prev, [key]: thumbnail }));
                    } else {
                      // Set to null to indicate we tried but no thumbnail found
                      console.log(`[DEBUG] Setting ${key} to null (no thumbnail found)`);
                      setOrderThumbnails(prev => ({ ...prev, [key]: null }));
                    }
                  }).catch(err => {
                    console.error(`[DEBUG] Failed to load thumbnail for ${key}:`, err);
                    setOrderThumbnails(prev => ({ ...prev, [key]: null }));
                  })
                );
              }
            });
          });
        });
      }
      
      // Load thumbnails for designs
      if (data.designs) {
        Object.keys(data.designs).forEach(categoryKey => {
          data.designs[categoryKey].forEach(design => {
            const key = `design-${design.id}`;
            // Check if thumbnail already exists
            const existingThumbnail = getThumbnailUrl(design);
            console.log(`[DEBUG] Design ${design.id} - existingThumbnail check:`, existingThumbnail ? `Found (length: ${existingThumbnail.length})` : 'Not found');
            if (existingThumbnail) {
              setOrderThumbnails(prev => ({ ...prev, [key]: existingThumbnail }));
            } else {
              // Try to fetch design to get thumbnail
              console.log(`[DEBUG] Fetching design ${design.id} from database for thumbnail`);
              thumbnailPromises.push(
                (async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(
                      `${import.meta.env.VITE_API_URL}/api/lamp-designs/admin/${design.id}`,
                      {
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      }
                    );
                    
                    console.log(`[DEBUG] Design ${design.id} fetch response status: ${response.status}`);
                    
                    if (response.ok) {
                      const fullDesign = await response.json();
                      const thumbnail = fullDesign.thumbnail;
                      
                      // Only use S3 URLs, filter out base64 thumbnails
                      if (isValidS3Url(thumbnail)) {
                        console.log(`[DEBUG] Design ${design.id} has valid S3 thumbnail: ${thumbnail.substring(0, 50)}...`);
                        setOrderThumbnails(prev => ({ ...prev, [key]: thumbnail }));
                        return;
                      } else if (thumbnail && thumbnail.startsWith('data:image')) {
                        console.warn(`[DEBUG] Design ${design.id} has base64 thumbnail, ignoring (only S3 URLs allowed)`);
                      } else {
                        console.log(`[DEBUG] Design ${design.id} has no valid thumbnail (missing or invalid format)`);
                      }
                    } else {
                      const errorText = await response.text();
                      console.warn(`[DEBUG] Design ${design.id} fetch failed with status ${response.status}:`, errorText);
                    }
                  } catch (error) {
                    console.error(`[DEBUG] Failed to fetch design ${design.id} for thumbnail:`, error);
                  }
                  // Set to null if no thumbnail found
                  console.log(`[DEBUG] Setting design ${design.id} thumbnail to null`);
                  setOrderThumbnails(prev => ({ ...prev, [key]: null }));
                })()
              );
            }
          });
        });
      }
      
      // Don't await - let thumbnails load in background
      Promise.all(thumbnailPromises).catch(err => {
        console.warn('Some thumbnails failed to load:', err);
      });
    } catch (err) {
      setError(err.message || 'Error searching');
    } finally {
      setLoading(false);
    }
  };

  const getVisibleItems = (items, categoryKey) => {
    const visibleCount = visibleCounts[categoryKey] || 20;
    return items.slice(0, visibleCount);
  };

  const handleShowMore = (categoryKey, totalCount) => {
    setVisibleCounts(prev => ({
      ...prev,
      [categoryKey]: (prev[categoryKey] || 20) + 20
    }));
  };

  const handleShowMoreRecent = () => {
    setRecentOrdersVisibleCount(prev => prev + 20);
  };

  const formatPrice = (amount) => {
    if (amount == null) return 'N/A';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const handleGenerateShippingDetails = (order) => {
    if (!order.shippingAddress) {
      alert('No shipping address available for this order.');
      return;
    }

    const shippingDetails = `
SHIPPING DETAILS
================

Order ID: ${order.orderId}
Date: ${new Date(order.createdAt).toLocaleDateString()}

SHIP TO:
${order.shippingAddress.name || 'N/A'}
${order.shippingAddress.address1 || ''}
${order.shippingAddress.address2 ? order.shippingAddress.address2 + '\n' : ''}${order.shippingAddress.city || ''}, ${order.shippingAddress.state || ''} ${order.shippingAddress.zip || ''}
${order.shippingAddress.country || ''}
${order.shippingAddress.email ? `Email: ${order.shippingAddress.email}` : ''}
${order.shippingAddress.phone ? `Phone: ${order.shippingAddress.phone}` : ''}

ITEMS:
${order.items?.map((item, index) => 
  `${index + 1}. ${item.name || `Item ${index + 1}`} (Qty: ${item.quantity || 1})`
).join('\n') || 'No items'}

TOTAL: ${formatPrice(order.totalAmount)}
    `.trim();

    // Create a blob and download as text file
    const blob = new Blob([shippingDetails], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shipping-details-${order.orderId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helper function to validate S3 URLs (filter out base64)
  const isValidS3Url = (url) => {
    return url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));
  };

  const getThumbnailUrl = (design) => {
    // Thumbnails are S3 URLs stored in designs only
    // Filter out base64 thumbnails - only return S3 URLs
    const thumbnail = design.thumbnail;
    if (isValidS3Url(thumbnail)) {
      return thumbnail;
    }
    if (thumbnail && thumbnail.startsWith('data:image')) {
      console.warn(`[ADMIN] Design ${design.id} has base64 thumbnail, ignoring (only S3 URLs allowed)`);
    }
    return null;
  };

  const getOrderItemThumbnail = async (item, orderId, itemIndex) => {
    // Thumbnails ONLY come from designs fetched by designId from LampDesigns table
    // NEVER check item.thumbnail or item.design.thumbnail - always fetch fresh from database
    const designId = item.designId;
    
    if (!designId) {
      console.warn(`[ADMIN] Item ${item.id || itemIndex} has no designId, cannot fetch thumbnail`);
      return null;
    }
    
    try {
      // Always fetch design from database - never use cached/populated design
      console.log(`[ADMIN] Fetching design ${designId} from database for thumbnail`);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/lamp-designs/admin/${designId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const design = await response.json();
        const thumbnail = design.thumbnail;
        
        // Only use S3 URLs, filter out base64 thumbnails
        if (isValidS3Url(thumbnail)) {
          console.log(`[ADMIN] Design ${designId} has valid S3 thumbnail: ${thumbnail.substring(0, 50)}...`);
          return thumbnail; // Return S3 URL from design
        } else if (thumbnail && thumbnail.startsWith('data:image')) {
          console.warn(`[ADMIN] Design ${designId} has base64 thumbnail, ignoring (only S3 URLs allowed)`);
        } else {
          console.warn(`[ADMIN] Design ${designId} has no valid thumbnail (missing or invalid format)`);
        }
      } else {
        const errorText = await response.text();
        console.error(`[ADMIN] Failed to fetch design ${designId}: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error(`[ADMIN] Failed to fetch design ${designId} for thumbnail:`, error);
    }
    
    return null;
  };

  const handleOpenOrderInBuilder = (orderId, itemIndex) => {
    navigate(`/builder?adminOrderId=${orderId}&itemIndex=${itemIndex}`);
  };

  const handleOpenDesignInBuilder = (designId) => {
    navigate(`/builder?designId=${designId}&adminMode=true`);
  };

  const hasDesign = (item) => {
    return item.designId || item.design || (item.baseShape && item.modifiers);
  };

  // Fetch recent orders when there's no search query
  useEffect(() => {
    const fetchRecentOrders = async () => {
      if (!searchQuery.trim() && !results) {
        setRecentOrdersLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/orders/admin/recent?limit=100`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.ok) {
            const data = await response.json();
            setRecentOrders(data);
            
            // Load thumbnails for recent orders
            const thumbnailPromises = [];
            data.forEach(order => {
              order.items?.forEach((item, itemIndex) => {
                if (hasDesign(item)) {
                  const key = `order-${order.orderId}-${itemIndex}`;
                  thumbnailPromises.push(
                    getOrderItemThumbnail(item, order.orderId, itemIndex).then(thumbnail => {
                      if (thumbnail) {
                        setOrderThumbnails(prev => ({ ...prev, [key]: thumbnail }));
                      } else {
                        setOrderThumbnails(prev => ({ ...prev, [key]: null }));
                      }
                    }).catch(err => {
                      console.error(`Failed to load thumbnail for ${key}:`, err);
                      setOrderThumbnails(prev => ({ ...prev, [key]: null }));
                    })
                  );
                }
              });
            });
            
            Promise.all(thumbnailPromises).catch(err => {
              console.warn('Some thumbnails failed to load:', err);
            });
          } else {
            console.error('Failed to fetch recent orders');
          }
        } catch (err) {
          console.error('Error fetching recent orders:', err);
        } finally {
          setRecentOrdersLoading(false);
        }
      } else {
        // Clear recent orders when searching
        setRecentOrders([]);
      }
    };

    fetchRecentOrders();
  }, [searchQuery, results]);

  const hasResults = results && (
    Object.keys(results.orders || {}).length > 0 || 
    Object.keys(results.designs || {}).length > 0
  );

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-error">You do not have permission to access this page.</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin - Search</h1>
        <p>Search across orders and lamp designs by ID, payment intent, or user identifier</p>
      </div>

      <form onSubmit={handleSearch} className="admin-search-form">
        <div className="input-group">
          <label htmlFor="search">Search</label>
          <input
            type="text"
            id="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter order ID, payment intent ID, design ID, user ID, or email"
            style={{ width: '100%' }}
          />
        </div>
        <button type="submit" disabled={loading || !searchQuery.trim()} className="search-button">
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && (
        <div className="admin-error">{error}</div>
      )}

      {hasResults && (
        <div className="admin-results">
          <h2>Search Results</h2>
          
          {/* Orders Results */}
          {results.orders && Object.keys(results.orders).length > 0 && (
            <div className="results-section">
              <h3>Orders</h3>
              
              {results.orders.byOrderId && results.orders.byOrderId.length > 0 && (
                <div className="results-category">
                  <h4>Matched by Order ID ({results.orders.byOrderId.length})</h4>
                  <div className="results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Thumbnail</th>
                          <th>Order ID</th>
                          <th>User ID</th>
                          <th>Date</th>
                          <th>Items</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVisibleItems(results.orders.byOrderId, 'orders.byOrderId').map((order) => (
                          <tr key={order.orderId}>
                            <td>
                              {order.items?.some(item => hasDesign(item)) ? (
                                <div className="order-thumbnails">
                                  {order.items?.map((item, index) => (
                                    hasDesign(item) && (
                                      <div key={index} className="order-item-thumbnail">
                                        {(() => {
                                          const thumbnailKey = `order-${order.orderId}-${index}`;
                                          const thumbnail = orderThumbnails[thumbnailKey];
                                          console.log(`[RENDER] Thumbnail for ${thumbnailKey}:`, {
                                            exists: thumbnail !== undefined,
                                            isNull: thumbnail === null,
                                            value: thumbnail ? thumbnail.substring(0, 50) + '...' : thumbnail,
                                            isValidS3: isValidS3Url(thumbnail),
                                            allKeys: Object.keys(orderThumbnails).filter(k => k.startsWith(`order-${order.orderId}`))
                                          });
                                          if (thumbnail && isValidS3Url(thumbnail)) {
                                            return (
                                              <div className="design-thumbnail">
                                                <img 
                                                  src={thumbnail} 
                                                  alt={`Item ${index + 1} thumbnail`}
                                                  crossOrigin="anonymous"
                                                  onError={(e) => {
                                                    console.error(`[RENDER] Image failed to load for ${thumbnailKey}:`, {
                                                      url: thumbnail,
                                                      error: e.target.error,
                                                      possibleCauses: [
                                                        'S3 bucket not publicly accessible',
                                                        'CORS not configured on S3 bucket',
                                                        'File does not exist at URL',
                                                        'Network error'
                                                      ]
                                                    });
                                                    // Show error placeholder instead of hiding
                                                    e.target.style.display = 'none';
                                                    const parent = e.target.parentElement;
                                                    if (parent) {
                                                      parent.innerHTML = '<div style="width: 80px; height: 80px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #999; font-size: 10px; text-align: center; padding: 5px;">Image Load Error</div>';
                                                    }
                                                  }}
                                                  onLoad={() => {
                                                    console.log(`[RENDER] Image loaded successfully for ${thumbnailKey}`);
                                                  }}
                                                />
                                              </div>
                                            );
                                          } else if (thumbnail === null) {
                                            return <div className="design-thumbnail-placeholder">No thumbnail</div>;
                                          } else {
                                            return <div className="design-thumbnail-placeholder">Loading...</div>;
                                          }
                                        })()}
                                      </div>
                                    )
                                  ))}
                                </div>
                              ) : (
                                <div className="design-thumbnail-placeholder">No design</div>
                              )}
                            </td>
                            <td>{order.orderId}</td>
                            <td>{order.userId || order.shippingAddress?.email || 'N/A'}</td>
                            <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                            <td>
                              <div className="order-items">
                                {order.items?.map((item, index) => (
                                  <div key={index} className="order-item">
                                    {item.name || `Item ${index + 1}`}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                {order.items?.map((item, index) => (
                                  hasDesign(item) && (
                                    <button
                                      key={index}
                                      onClick={() => handleOpenOrderInBuilder(order.orderId, index)}
                                      className="open-builder-button"
                                    >
                                      Open Item {index + 1}
                                    </button>
                                  )
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {results.orders.byOrderId.length > (visibleCounts['orders.byOrderId'] || 20) && (
                    <button
                      onClick={() => handleShowMore('orders.byOrderId', results.orders.byOrderId.length)}
                      className="show-more-button"
                    >
                      Show More ({results.orders.byOrderId.length - (visibleCounts['orders.byOrderId'] || 20)} remaining)
                    </button>
                  )}
                </div>
              )}

              {results.orders.byPaymentIntentId && results.orders.byPaymentIntentId.length > 0 && (
                <div className="results-category">
                  <h4>Matched by Payment Intent ID ({results.orders.byPaymentIntentId.length})</h4>
                  <div className="results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Thumbnail</th>
                          <th>Order ID</th>
                          <th>Payment Intent ID</th>
                          <th>User ID</th>
                          <th>Date</th>
                          <th>Items</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVisibleItems(results.orders.byPaymentIntentId, 'orders.byPaymentIntentId').map((order) => (
                          <tr key={order.orderId}>
                            <td>
                              {order.items?.some(item => hasDesign(item)) ? (
                                <div className="order-thumbnails">
                                  {order.items?.map((item, index) => (
                                    hasDesign(item) && (
                                      <div key={index} className="order-item-thumbnail">
                                        {(() => {
                                          const thumbnailKey = `order-${order.orderId}-${index}`;
                                          const thumbnail = orderThumbnails[thumbnailKey];
                                          console.log(`[RENDER] Thumbnail for ${thumbnailKey}:`, {
                                            exists: thumbnail !== undefined,
                                            isNull: thumbnail === null,
                                            value: thumbnail ? thumbnail.substring(0, 50) + '...' : thumbnail,
                                            isValidS3: isValidS3Url(thumbnail),
                                            allKeys: Object.keys(orderThumbnails).filter(k => k.startsWith(`order-${order.orderId}`))
                                          });
                                          if (thumbnail && isValidS3Url(thumbnail)) {
                                            return (
                                              <div className="design-thumbnail">
                                                <img 
                                                  src={thumbnail} 
                                                  alt={`Item ${index + 1} thumbnail`}
                                                  crossOrigin="anonymous"
                                                  onError={(e) => {
                                                    console.error(`[RENDER] Image failed to load for ${thumbnailKey}:`, {
                                                      url: thumbnail,
                                                      error: e.target.error,
                                                      possibleCauses: [
                                                        'S3 bucket not publicly accessible',
                                                        'CORS not configured on S3 bucket',
                                                        'File does not exist at URL',
                                                        'Network error'
                                                      ]
                                                    });
                                                    // Show error placeholder instead of hiding
                                                    e.target.style.display = 'none';
                                                    const parent = e.target.parentElement;
                                                    if (parent) {
                                                      parent.innerHTML = '<div style="width: 80px; height: 80px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #999; font-size: 10px; text-align: center; padding: 5px;">Image Load Error</div>';
                                                    }
                                                  }}
                                                  onLoad={() => {
                                                    console.log(`[RENDER] Image loaded successfully for ${thumbnailKey}`);
                                                  }}
                                                />
                                              </div>
                                            );
                                          } else if (thumbnail === null) {
                                            return <div className="design-thumbnail-placeholder">No thumbnail</div>;
                                          } else {
                                            return <div className="design-thumbnail-placeholder">Loading...</div>;
                                          }
                                        })()}
                                      </div>
                                    )
                                  ))}
                                </div>
                              ) : (
                                <div className="design-thumbnail-placeholder">No design</div>
                              )}
                            </td>
                            <td>{order.orderId}</td>
                            <td>{order.paymentIntentId}</td>
                            <td>{order.userId || order.shippingAddress?.email || 'N/A'}</td>
                            <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                            <td>
                              <div className="order-items">
                                {order.items?.map((item, index) => (
                                  <div key={index} className="order-item">
                                    {item.name || `Item ${index + 1}`}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                {order.items?.map((item, index) => (
                                  hasDesign(item) && (
                                    <button
                                      key={index}
                                      onClick={() => handleOpenOrderInBuilder(order.orderId, index)}
                                      className="open-builder-button"
                                    >
                                      Open Item {index + 1}
                                    </button>
                                  )
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {results.orders.byPaymentIntentId.length > (visibleCounts['orders.byPaymentIntentId'] || 20) && (
                    <button
                      onClick={() => handleShowMore('orders.byPaymentIntentId', results.orders.byPaymentIntentId.length)}
                      className="show-more-button"
                    >
                      Show More ({results.orders.byPaymentIntentId.length - (visibleCounts['orders.byPaymentIntentId'] || 20)} remaining)
                    </button>
                  )}
                </div>
              )}

              {results.orders.byUserId && results.orders.byUserId.length > 0 && (
                <div className="results-category">
                  <h4>Matched by User ID ({results.orders.byUserId.length})</h4>
                  <div className="results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Thumbnail</th>
                          <th>Order ID</th>
                          <th>User ID</th>
                          <th>Date</th>
                          <th>Items</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVisibleItems(results.orders.byUserId, 'orders.byUserId').map((order) => (
                          <tr key={order.orderId}>
                            <td>
                              {order.items?.some(item => hasDesign(item)) ? (
                                <div className="order-thumbnails">
                                  {order.items?.map((item, index) => (
                                    hasDesign(item) && (
                                      <div key={index} className="order-item-thumbnail">
                                        {(() => {
                                          const thumbnailKey = `order-${order.orderId}-${index}`;
                                          const thumbnail = orderThumbnails[thumbnailKey];
                                          console.log(`[RENDER] Thumbnail for ${thumbnailKey}:`, {
                                            exists: thumbnail !== undefined,
                                            isNull: thumbnail === null,
                                            value: thumbnail ? thumbnail.substring(0, 50) + '...' : thumbnail,
                                            isValidS3: isValidS3Url(thumbnail),
                                            allKeys: Object.keys(orderThumbnails).filter(k => k.startsWith(`order-${order.orderId}`))
                                          });
                                          if (thumbnail && isValidS3Url(thumbnail)) {
                                            return (
                                              <div className="design-thumbnail">
                                                <img 
                                                  src={thumbnail} 
                                                  alt={`Item ${index + 1} thumbnail`}
                                                  crossOrigin="anonymous"
                                                  onError={(e) => {
                                                    console.error(`[RENDER] Image failed to load for ${thumbnailKey}:`, {
                                                      url: thumbnail,
                                                      error: e.target.error,
                                                      possibleCauses: [
                                                        'S3 bucket not publicly accessible',
                                                        'CORS not configured on S3 bucket',
                                                        'File does not exist at URL',
                                                        'Network error'
                                                      ]
                                                    });
                                                    // Show error placeholder instead of hiding
                                                    e.target.style.display = 'none';
                                                    const parent = e.target.parentElement;
                                                    if (parent) {
                                                      parent.innerHTML = '<div style="width: 80px; height: 80px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #999; font-size: 10px; text-align: center; padding: 5px;">Image Load Error</div>';
                                                    }
                                                  }}
                                                  onLoad={() => {
                                                    console.log(`[RENDER] Image loaded successfully for ${thumbnailKey}`);
                                                  }}
                                                />
                                              </div>
                                            );
                                          } else if (thumbnail === null) {
                                            return <div className="design-thumbnail-placeholder">No thumbnail</div>;
                                          } else {
                                            return <div className="design-thumbnail-placeholder">Loading...</div>;
                                          }
                                        })()}
                                      </div>
                                    )
                                  ))}
                                </div>
                              ) : (
                                <div className="design-thumbnail-placeholder">No design</div>
                              )}
                            </td>
                            <td>{order.orderId}</td>
                            <td>{order.userId || order.shippingAddress?.email || 'N/A'}</td>
                            <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                            <td>
                              <div className="order-items">
                                {order.items?.map((item, index) => (
                                  <div key={index} className="order-item">
                                    {item.name || `Item ${index + 1}`}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                {order.items?.map((item, index) => (
                                  hasDesign(item) && (
                                    <button
                                      key={index}
                                      onClick={() => handleOpenOrderInBuilder(order.orderId, index)}
                                      className="open-builder-button"
                                    >
                                      Open Item {index + 1}
                                    </button>
                                  )
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {results.orders.byUserId.length > (visibleCounts['orders.byUserId'] || 20) && (
                    <button
                      onClick={() => handleShowMore('orders.byUserId', results.orders.byUserId.length)}
                      className="show-more-button"
                    >
                      Show More ({results.orders.byUserId.length - (visibleCounts['orders.byUserId'] || 20)} remaining)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Designs Results */}
          {results.designs && Object.keys(results.designs).length > 0 && (
            <div className="results-section">
              <h3>Lamp Designs</h3>
              
              {results.designs.byId && results.designs.byId.length > 0 && (
                <div className="results-category">
                  <h4>Matched by Design ID ({results.designs.byId.length})</h4>
                  <div className="results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Thumbnail</th>
                          <th>Design ID</th>
                          <th>Name</th>
                          <th>Created By</th>
                          <th>User ID</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVisibleItems(results.designs.byId, 'designs.byId').map((design) => {
                          const thumbnailKey = `design-${design.id}`;
                          const thumbnail = orderThumbnails[thumbnailKey] !== undefined 
                            ? orderThumbnails[thumbnailKey] 
                            : getThumbnailUrl(design);
                          return (
                          <tr key={design.id}>
                            <td>
                              {thumbnail ? (
                                <div className="design-thumbnail">
                                  <img 
                                    src={thumbnail} 
                                    alt={design.name || 'Design thumbnail'}
                                  />
                                </div>
                              ) : (
                                <div className="design-thumbnail-placeholder">
                                  {orderThumbnails[thumbnailKey] === null ? 'No thumbnail' : 'Loading...'}
                                </div>
                              )}
                            </td>
                            <td>{design.id}</td>
                            <td>{design.name || 'Untitled'}</td>
                            <td>{design.createdBy || 'N/A'}</td>
                            <td>{design.userId || 'N/A'}</td>
                            <td>{new Date(design.createdAt).toLocaleDateString()}</td>
                            <td>
                              <button
                                onClick={() => handleOpenDesignInBuilder(design.id)}
                                className="open-builder-button"
                              >
                                Open in Builder
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {results.designs.byId.length > (visibleCounts['designs.byId'] || 20) && (
                    <button
                      onClick={() => handleShowMore('designs.byId', results.designs.byId.length)}
                      className="show-more-button"
                    >
                      Show More ({results.designs.byId.length - (visibleCounts['designs.byId'] || 20)} remaining)
                    </button>
                  )}
                </div>
              )}

              {results.designs.byCreatedBy && results.designs.byCreatedBy.length > 0 && (
                <div className="results-category">
                  <h4>Matched by Created By ({results.designs.byCreatedBy.length})</h4>
                  <div className="results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Thumbnail</th>
                          <th>Design ID</th>
                          <th>Name</th>
                          <th>Created By</th>
                          <th>User ID</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVisibleItems(results.designs.byCreatedBy, 'designs.byCreatedBy').map((design) => {
                          const thumbnailKey = `design-${design.id}`;
                          const thumbnail = orderThumbnails[thumbnailKey] !== undefined 
                            ? orderThumbnails[thumbnailKey] 
                            : getThumbnailUrl(design);
                          return (
                          <tr key={design.id}>
                            <td>
                              {thumbnail ? (
                                <div className="design-thumbnail">
                                  <img 
                                    src={thumbnail} 
                                    alt={design.name || 'Design thumbnail'}
                                  />
                                </div>
                              ) : (
                                <div className="design-thumbnail-placeholder">
                                  {orderThumbnails[thumbnailKey] === null ? 'No thumbnail' : 'Loading...'}
                                </div>
                              )}
                            </td>
                            <td>{design.id}</td>
                            <td>{design.name || 'Untitled'}</td>
                            <td>{design.createdBy || 'N/A'}</td>
                            <td>{design.userId || 'N/A'}</td>
                            <td>{new Date(design.createdAt).toLocaleDateString()}</td>
                            <td>
                              <button
                                onClick={() => handleOpenDesignInBuilder(design.id)}
                                className="open-builder-button"
                              >
                                Open in Builder
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {results.designs.byCreatedBy.length > (visibleCounts['designs.byCreatedBy'] || 20) && (
                    <button
                      onClick={() => handleShowMore('designs.byCreatedBy', results.designs.byCreatedBy.length)}
                      className="show-more-button"
                    >
                      Show More ({results.designs.byCreatedBy.length - (visibleCounts['designs.byCreatedBy'] || 20)} remaining)
                    </button>
                  )}
                </div>
              )}

              {results.designs.byUserId && results.designs.byUserId.length > 0 && (
                <div className="results-category">
                  <h4>Matched by User ID ({results.designs.byUserId.length})</h4>
                  <div className="results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Thumbnail</th>
                          <th>Design ID</th>
                          <th>Name</th>
                          <th>Created By</th>
                          <th>User ID</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVisibleItems(results.designs.byUserId, 'designs.byUserId').map((design) => {
                          const thumbnailKey = `design-${design.id}`;
                          const thumbnail = orderThumbnails[thumbnailKey] !== undefined 
                            ? orderThumbnails[thumbnailKey] 
                            : getThumbnailUrl(design);
                          return (
                          <tr key={design.id}>
                            <td>
                              {thumbnail ? (
                                <div className="design-thumbnail">
                                  <img 
                                    src={thumbnail} 
                                    alt={design.name || 'Design thumbnail'}
                                  />
                                </div>
                              ) : (
                                <div className="design-thumbnail-placeholder">
                                  {orderThumbnails[thumbnailKey] === null ? 'No thumbnail' : 'Loading...'}
                                </div>
                              )}
                            </td>
                            <td>{design.id}</td>
                            <td>{design.name || 'Untitled'}</td>
                            <td>{design.createdBy || 'N/A'}</td>
                            <td>{design.userId || 'N/A'}</td>
                            <td>{new Date(design.createdAt).toLocaleDateString()}</td>
                            <td>
                              <button
                                onClick={() => handleOpenDesignInBuilder(design.id)}
                                className="open-builder-button"
                              >
                                Open in Builder
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {results.designs.byUserId.length > (visibleCounts['designs.byUserId'] || 20) && (
                    <button
                      onClick={() => handleShowMore('designs.byUserId', results.designs.byUserId.length)}
                      className="show-more-button"
                    >
                      Show More ({results.designs.byUserId.length - (visibleCounts['designs.byUserId'] || 20)} remaining)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!loading && results && !hasResults && (
        <div className="admin-empty">
          <p>No results found for "{searchQuery}"</p>
        </div>
      )}

      {!loading && !results && !error && !searchQuery.trim() && (
        <div className="admin-results">
          <h2>Recent Orders</h2>
          
          {recentOrdersLoading ? (
            <div className="admin-loading">Loading recent orders...</div>
          ) : recentOrders.length > 0 ? (
            <div className="results-section">
              <div className="results-category">
                <h4>Last {recentOrders.length} Orders</h4>
                <div className="results-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Thumbnail</th>
                        <th>Order ID</th>
                        <th>User</th>
                        <th>Date</th>
                        <th>Cost</th>
                        <th>Items</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.slice(0, recentOrdersVisibleCount).map((order) => (
                        <tr key={order.orderId}>
                          <td>
                            {order.items?.some(item => hasDesign(item)) ? (
                              <div className="order-thumbnails">
                                {order.items?.map((item, index) => (
                                  hasDesign(item) && (
                                    <div key={index} className="order-item-thumbnail">
                                      {(() => {
                                        const thumbnailKey = `order-${order.orderId}-${index}`;
                                        const thumbnail = orderThumbnails[thumbnailKey];
                                        if (thumbnail) {
                                          return (
                                            <div className="design-thumbnail">
                                              <img 
                                                src={thumbnail} 
                                                alt={`Item ${index + 1} thumbnail`}
                                              />
                                            </div>
                                          );
                                        } else if (thumbnail === null) {
                                          return <div className="design-thumbnail-placeholder">No thumbnail</div>;
                                        } else {
                                          return <div className="design-thumbnail-placeholder">Loading...</div>;
                                        }
                                      })()}
                                    </div>
                                  )
                                ))}
                              </div>
                            ) : (
                              <div className="design-thumbnail-placeholder">No design</div>
                            )}
                          </td>
                          <td>{order.orderId}</td>
                          <td>
                            {order.isGuest || (order.userId && order.userId.startsWith('guest_')) ? (
                              <>
                                <span style={{ color: '#999', fontStyle: 'italic' }}>Guest</span>
                                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                                  {order.userDisplayName || order.shippingAddress?.email || order.userId || 'N/A'}
                                </div>
                              </>
                            ) : (
                              <>
                                {order.userDisplayName || order.shippingAddress?.email || 'N/A'}
                                {order.userId && order.userDisplayName !== order.userId && (
                                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                                    {order.userId}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                          <td>{formatPrice(order.totalAmount)}</td>
                          <td>
                            <div className="order-items">
                              {order.items?.map((item, index) => (
                                <div key={index} className="order-item">
                                  {item.name || `Item ${index + 1}`}
                                </div>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                onClick={() => handleGenerateShippingDetails(order)}
                                className="open-builder-button"
                                style={{ marginBottom: '5px' }}
                              >
                                Generate Shipping Details
                              </button>
                              {order.items?.map((item, index) => (
                                hasDesign(item) && (
                                  <button
                                    key={index}
                                    onClick={() => handleOpenOrderInBuilder(order.orderId, index)}
                                    className="open-builder-button"
                                  >
                                    Open Item {index + 1}
                                  </button>
                                )
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {recentOrders.length > recentOrdersVisibleCount && (
                  <button
                    onClick={handleShowMoreRecent}
                    className="show-more-button"
                  >
                    See More ({recentOrders.length - recentOrdersVisibleCount} remaining)
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="admin-empty">
              <p>No recent orders found.</p>
            </div>
          )}
        </div>
      )}

      {!loading && !results && !error && searchQuery.trim() && (
        <div className="admin-empty">
          <p>Enter a search query and click Search.</p>
        </div>
      )}
    </div>
  );
};

export default Admin;

